import { useMemo } from 'react'
import { differenceInCalendarDays } from 'date-fns'
import { AbsenceDayRequest, AbsenceOccurrence } from '../../../types/absence'

const SICK_NOTE_DAY_THRESHOLD = 7

interface UseSickNoteReminderArgs {
  selectedDays: AbsenceDayRequest[] | null | undefined
  extendAbsenceData?: AbsenceOccurrence | null
  isSickness: boolean
  isUKEmployee: boolean
}

interface UseSickNoteReminderResult {
  shouldShowSickNoteReminder: boolean
}

const findFirstCheckedDay = (days: AbsenceDayRequest[]): Date | undefined =>
  days.find(({ checked }) => checked)?.day

const findLastCheckedDay = (days: AbsenceDayRequest[]): Date | undefined => {
  for (let i = days.length - 1; i >= 0; i -= 1) {
    if (days[i].checked) return days[i].day
  }
  return undefined
}

export const useSickNoteReminder = ({
  selectedDays,
  extendAbsenceData,
  isSickness,
  isUKEmployee,
}: UseSickNoteReminderArgs): UseSickNoteReminderResult => {
  const shouldShowSickNoteReminder = useMemo(() => {
    if (!isUKEmployee || !isSickness) return false
    if (!selectedDays || selectedDays.length === 0) return false

    const lastCheckedDay = findLastCheckedDay(selectedDays)
    if (!lastCheckedDay) return false

    const firstDate = extendAbsenceData
      ? new Date(extendAbsenceData.startDate)
      : findFirstCheckedDay(selectedDays)
    if (!firstDate) return false

    const calendarSpan = differenceInCalendarDays(lastCheckedDay, firstDate) + 1
    return calendarSpan > SICK_NOTE_DAY_THRESHOLD
  }, [selectedDays, extendAbsenceData, isSickness, isUKEmployee])

  return { shouldShowSickNoteReminder }
}
